import { pb } from './pb';

export type LogoutReason = 'session_expired' | 'password_changed' | 'tenant_suspended';

const REASON_KEY = 'focus_logout_reason';

/** Mensajes en login (español). */
export const LOGOUT_REASON_COPY: Record<LogoutReason, string> = {
  session_expired:
    'Tu sesión expiró o ya no es válida. Volvé a entrar para continuar.',
  password_changed:
    'Cambiaste tu contraseña. Entrá de nuevo con la nueva clave.',
  tenant_suspended:
    'Este cliente está suspendido. El acceso está deshabilitado.',
};

let guardInstalled = false;
let loggingOut = false;

/**
 * Cierra la sesión y manda a /login con un mensaje claro.
 * Full navigation: limpia React Query y evita pantallas vacías con JWT muerto.
 */
export function forceLogout(reason: LogoutReason = 'session_expired'): void {
  if (loggingOut) return;
  loggingOut = true;
  try {
    try {
      sessionStorage.setItem(REASON_KEY, reason);
    } catch {
      /* ignore */
    }
    pb.authStore.clear();
  } finally {
    const path = typeof window !== 'undefined' ? window.location.pathname : '';
    if (path.startsWith('/login')) {
      loggingOut = false;
      return;
    }
    const q = new URLSearchParams({ reason });
    window.location.assign(`/login?${q.toString()}`);
  }
}

export function consumeLogoutReason(): LogoutReason | null {
  const fromUrl =
    typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search).get('reason')
      : null;
  let fromStore: string | null = null;
  try {
    fromStore = sessionStorage.getItem(REASON_KEY);
    sessionStorage.removeItem(REASON_KEY);
  } catch {
    /* ignore */
  }
  const raw = fromUrl || fromStore;
  if (raw === 'session_expired' || raw === 'password_changed' || raw === 'tenant_suspended') {
    return raw;
  }
  return null;
}

export function isTenantSuspendedMessage(message: string): boolean {
  return /suspendido|ya no existe/i.test(message);
}

function isAuthEndpoint(url: string): boolean {
  return (
    url.includes('/auth-with-password') ||
    url.includes('/auth-with-oauth') ||
    url.includes('/auth-with-otp') ||
    url.includes('/request-password-reset') ||
    url.includes('/confirm-password-reset') ||
    url.includes('/request-verification') ||
    url.includes('/confirm-verification')
  );
}

/**
 * Si cualquier request autenticado recibe 401, el JWT ya no sirve
 * (p.ej. cambiaron la clave). Antes fallaban las queries en silencio →
 * pantallas vacías hasta el authRefresh de 60s. Ahora salimos al toque.
 */
export function installAuthExpiryGuard(): void {
  if (guardInstalled) return;
  guardInstalled = true;

  const prev = pb.afterSend;
  pb.afterSend = async (response, data, options?) => {
    const next = prev ? await prev(response, data, options) : data;
    if ((response.status === 401 || response.status === 403) && pb.authStore.token) {
      const url = response.url || '';
      if (!isAuthEndpoint(url)) {
        const message =
          data && typeof data === 'object' && 'message' in data
            ? String((data as { message?: unknown }).message ?? '')
            : '';
        if (response.status === 403 && isTenantSuspendedMessage(message)) {
          forceLogout('tenant_suspended');
        } else if (response.status === 401) {
          forceLogout('session_expired');
        }
      }
    }
    return next;
  };
}
